import { useEffect, useRef, useState } from "react";
import { ArrowUpRight, Play } from "lucide-react";
import { t, useLocale } from "@/i18n/locale";
import { Message } from "@/i18n/Message";
import { LandingVideo } from "@/components/landing/LandingDemoMedia";
import AppCardVisual from "@/components/store/AppCardVisual";
import { AppIcon } from "../AppIcon";
import DeveloperAttribution from "./DeveloperAttribution";
import RecordedAppPreview from "./RecordedAppPreview";
import type { EditorialMedia } from "./discoveryEditorial";
import heroVideoMp4 from "./hero-fields/hero-bg.mp4";
import heroVideoWebm from "./hero-fields/hero-bg.webm";
import heroVideoPoster from "./hero-fields/hero-bg-poster.webp";
import type { EditorialSlots, HeroAppCard } from "./useDiscoveryEditorial";
import type { DiscoveryCardModel } from "./types";

const sources = (media: EditorialMedia) => [
  { src: media.webm, type: "video/webm" },
  { src: media.mp4, type: "video/mp4" },
];

/**
 * An app's recorded preview, playing whenever it is on screen (LandingVideo
 * pauses it off screen and keeps the poster for reduced motion). Apps without
 * a recording keep the hover-to-play still.
 */
function AppPreviewVideo({ card, alt = "" }: { card: DiscoveryCardModel; alt?: string }) {
  const recording = card.recordedPreview;
  if (!recording) return <RecordedAppPreview entry={card} />;
  return <LandingVideo key={recording.url} sources={[{ src: recording.url, type: recording.content_type }]}
    poster={recording.poster_url || card.thumbnailUrl || ""} alt={alt} variant="discover" />;
}

function CardIcon({ card, size }: { card: DiscoveryCardModel; size: number }) {
  return <AppIcon name={card.title} seed={card.selection.id} size={size} listingIconUrl={card.iconUrl} />;
}

/**
 * The hero's right column: one app, recorded on its own, with its name and
 * what it does. The purpose switch is manual — never an auto-advancing carousel.
 */
export function HeroDemoPanel({ demos, onOpen }: {
  demos: EditorialSlots["demos"];
  onOpen: (card: DiscoveryCardModel) => void;
}) {
  useLocale();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const demo = demos.find((item) => item.id === selectedId) ?? demos[0];
  if (!demo) return null;
  const openLabel = t("Open {{value0}}", { value0: demo.card.title });
  return (
    <div className="discovery-hero-demo">
      <button type="button" className="discovery-intro-demo" aria-label={openLabel} onClick={() => onOpen(demo.card)}>
        <LandingVideo key={demo.id} sources={sources(demo.media)} poster={demo.media.poster}
          alt={t("Demo of {{value0}}", { value0: demo.card.title })} variant="discover" />
      </button>
      <div className="discovery-hero-demo-caption">
        <CardIcon card={demo.card} size={36} />
        <span className="discovery-hero-demo-text">
          <strong>{demo.card.title}</strong>
          <span>{t(demo.useCase)}</span>
        </span>
        {demos.length > 1 ? (
          <div className="ato-segmented discovery-hero-demo-switch" role="group" aria-label={t("Demo")}>
            {demos.map((item) => (
              <button key={item.id} type="button" aria-pressed={item.id === demo.id} onClick={() => setSelectedId(item.id)}>
                {t(item.label)}
              </button>
            ))}
          </div>
        ) : null}
        <button type="button" className="ato-button ato-button--soft" aria-label={openLabel} onClick={() => onOpen(demo.card)}>
          <Play size={16} aria-hidden="true" />
          <span className="discovery-hero-demo-open-full">{openLabel}</span>
          <span className="discovery-hero-demo-open-short" aria-hidden="true"><Message message="Open" /></span>
        </button>
      </div>
    </div>
  );
}

const WIDE_SCREEN = "(min-width: 900px)";
export const matchesWideScreen = () =>
  typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(WIDE_SCREEN).matches;

/** Wide screens show the hero cards and keep app actions in the window bar;
 *  narrow ones keep the single demo and the summary rows. */
export function useWideScreen() {
  const [wide, setWide] = useState(matchesWideScreen);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const query = window.matchMedia(WIDE_SCREEN);
    const onChange = () => setWide(query.matches);
    onChange();
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);
  return wide;
}

/**
 * The wide-screen hero's right side: the most popular apps, each playing its
 * recording on its own soft colour field. The card is drawn like Library's
 * Discover apps cards: the media fills it, and the icon, name and description
 * sit on a scrim at its foot. A card opens its app; there is nothing else to
 * choose.
 */
const GENRE_LABELS: Record<string, string> = { apps: "Apps", games: "Games", media: "Media", tools: "Tools", ai: "AI" };
const genre = (card: DiscoveryCardModel) => (card.category ? GENRE_LABELS[card.category] ?? null : null);

/** Resolves once every URL has loaded (or failed), or after `timeoutMs`. */
function useImagesReady(urls: readonly string[], timeoutMs = 4000): boolean {
  const key = urls.join("\n");
  const [readyKey, setReadyKey] = useState<string | null>(null);
  useEffect(() => {
    if (!key) return;
    let cancelled = false;
    const done = () => { if (!cancelled) setReadyKey(key); };
    const timer = setTimeout(done, timeoutMs);
    void Promise.all(key.split("\n").map((url) => new Promise<void>((resolve) => {
      const image = new Image();
      image.onload = image.onerror = () => resolve();
      image.src = url;
    }))).then(done);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [key, timeoutMs]);
  return !key || readyKey === key;
}

/** Card-shaped placeholders: three framed squares with a caption line. */
function HeroCardsSkeleton() {
  return (
    <ul className="discovery-hero-cards discovery-hero-cards--skeleton" aria-hidden="true">
      {[0, 1, 2].map((index) => (
        <li key={index} className="discovery-hero-card"><span className="discovery-hero-skeleton-frame" /><span className="discovery-hero-skeleton-caption" /></li>
      ))}
    </ul>
  );
}

/**
 * The hero's apps appear together, never one by one: a skeleton stands in
 * until the list has answered and every card's still has loaded (or 4 s have
 * passed), then all three fade in at once. Meanwhile the real cards are
 * mounted but hidden and inert, so their recordings start loading early.
 */
export function HeroAppCards({ cards, onOpen }: {
  cards: readonly HeroAppCard[] | null;
  onOpen: (card: DiscoveryCardModel) => void;
}) {
  useLocale();
  const stills = (cards ?? []).flatMap(({ card }) => {
    const url = card.recordedPreview?.poster_url || card.thumbnailUrl;
    return url ? [url] : [];
  });
  const loaded = useImagesReady(stills);
  const ready = cards !== null && loaded;
  const list = useRef<HTMLUListElement>(null);
  useEffect(() => {
    if (!list.current) return;
    if (ready) list.current.removeAttribute("inert");
    else list.current.setAttribute("inert", "");
  }, [ready, cards]);
  return (
    <div className={`discovery-hero-cards-stage${ready ? " discovery-hero-cards-stage--ready" : ""}`}>
      {ready ? null : <HeroCardsSkeleton />}
      {cards ? (
        <ul ref={list} className="discovery-hero-cards discovery-hero-cards--live" aria-hidden={ready ? undefined : true}>
          {cards.map((item) => {
            const name = item.name ?? item.card.title;
            return (
              <li key={item.listingId} className="discovery-hero-card">
                <button type="button" aria-label={t("Open {{value0}}", { value0: name })} onClick={() => onOpen(item.card)}>
                  <AppCardVisual title={name} description={item.card.description} iconUrl={item.icon ?? item.card.iconUrl}
                    fallbackKey={item.listingId} media={<AppPreviewVideo card={item.card} />}
                    descriptionClassName="discovery-hero-card-description" />
                  <span className="discovery-hero-card-try" aria-hidden="true"><Message message="Try now" /><ArrowUpRight size={16} /></span>
                </button>
                <p className="discovery-hero-card-caption" aria-hidden="true">
                  <strong>{name}</strong>{genre(item.card) ? <> · {t(genre(item.card)!)}</> : null}
                </p>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

/** The hero's moving backdrop: dark silk with slowly drifting coloured
 *  light (a 20 s forward-then-reverse loop, so it never jumps). LandingVideo
 *  pauses it off screen and shows the still under reduced motion. */
export function HeroVideoBackdrop() {
  return (
    <div className="discovery-hero-video" aria-hidden="true">
      <LandingVideo sources={[{ src: heroVideoWebm, type: "video/webm" }, { src: heroVideoMp4, type: "video/mp4" }]}
        poster={heroVideoPoster} alt="" variant="discover" />
    </div>
  );
}

/** The hero's screen-saver light: blurred colour fields drifting over the
 *  fabric (see .discovery-hero-fluid). Decorative only. */
export function FluidBackdrop() {
  return <div className="discovery-hero-fluid" aria-hidden="true"><span /><span /><span /><span /></div>;
}

type Feature = EditorialSlots["spotlights"][number];

/**
 * Featured: one curated app as a full-width row between grid lines, one card
 * line tall. Several features are several rows (see exploreRows).
 */
export function FeaturedRow({ feature, mirrored = false, onOpen, onNavigate }: {
  feature: Feature;
  /** Media on the left and copy on the right (rows alternate). */
  mirrored?: boolean;
  onOpen: (card: DiscoveryCardModel) => void;
  onNavigate?: (route: string) => void;
}) {
  useLocale();
  const { card } = feature;
  const openLabel = t("Open {{value0}}", { value0: card.title });
  return (
    <section className={`discovery-featured-row${mirrored ? " discovery-featured-row--mirrored" : ""}`} aria-label={t("Featured")}>
      <FluidBackdrop />
      <div className="discovery-featured-row-body">
        <div className="discovery-featured-row-top">
          <span className="discovery-featured-row-badge"><Message message="Featured" /></span>
        </div>
        <h3>{t(feature.headline)}</h3>
        <p>{t(feature.summary)}</p>
        <div className="discovery-featured-row-footer">
          <span className="discovery-spotlight-app">
            <CardIcon card={card} size={40} />
            <span>
              <strong>{card.title}</strong>
              <DeveloperAttribution developers={card.developers ?? []} source={card.source}
                sourceTarget={card.selection.kind === "discover" ? card.selection : undefined} onNavigate={onNavigate} compact />
            </span>
          </span>
          <button type="button" className="ato-button ato-button--outline" aria-label={openLabel} onClick={() => onOpen(card)}>
            <Play size={16} aria-hidden="true" /><Message message="Run" />
          </button>
        </div>
      </div>
      <button type="button" className="discovery-featured-row-media" aria-label={openLabel} onClick={() => onOpen(card)}>
        {feature.media ? (
          <LandingVideo sources={sources(feature.media)} poster={feature.media.poster}
            alt={t("Demo of {{value0}}", { value0: card.title })} variant="discover" />
        ) : <AppPreviewVideo card={card} alt={t("Demo of {{value0}}", { value0: card.title })} />}
      </button>
    </section>
  );
}
